import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  LinearProgress,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  RemoveCircleOutline as DecommissionIcon,
  Pets as PetsIcon,
  EggAlt as EggIcon,
  FavoriteBorder as AliveIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { LocalDining as FeedIcon } from '@mui/icons-material';
import {
  useFeedTypes,
  useFeedBrands,
  useFeedProcurements,
  useFeedProductions,
} from '../hooks/useFeed';
import {
  useBirds,
  useCreateBird,
  useDeleteBird,
  useBirdsStats,
  useUpdateFeedInfo,
} from '../hooks/useBirds';
import { useCreateDecommission } from '../hooks/useDecommissions';
import { Bird } from '../types';

const BirdsPage: React.FC = () => {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBird, setSelectedBird] = useState<Bird | null>(null);
  const [formData, setFormData] = useState({
    procurement_id: '',
    name: '',
    type: '',
    color: '',
    quantity: '',
  });

  // React Query hooks
  const { data: birdsData, isLoading, error, refetch, isFetching: birdsFetching } = useBirds();
  const { data: stats, isFetching: statsFetching } = useBirdsStats();
  const createMutation = useCreateBird();
  const deleteMutation = useDeleteBird();
  const updateFeedInfoMutation = useUpdateFeedInfo();
  const createDecommissionMutation = useCreateDecommission();
  const { data: feedTypes } = useFeedTypes();
  const { data: feedBrands } = useFeedBrands();
  const { data: feedProcurements } = useFeedProcurements({ per_page: 100 });
  const { data: feedProductions } = useFeedProductions({});

  const handleOpenDialog = (bird?: Bird) => {
    if (bird) {
      setSelectedBird(bird);
      setFormData({
        procurement_id: bird.procurement_id.toString(),
        name: bird.name,
        type: bird.birds_type,
        color: bird.color,
        quantity: bird.current_quantity.toString(),
      });
    } else {
      setSelectedBird(null);
      setFormData({
        procurement_id: '',
        name: '',
        type: '',
        color: '',
        quantity: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBird(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      const data = {
        procurement_id: parseInt(formData.procurement_id),
        name: formData.name,
        type: formData.type,
        color: formData.color,
        quantity: parseInt(formData.quantity),
      };

      await createMutation.mutateAsync(data);
      handleCloseDialog();
      refetch();
    } catch (error) {
      console.error('Error creating bird:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this bird batch?')) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        console.error('Error deleting bird:', error);
      }
    }
  };

  // Feed assignment dialog
  const [openFeedDialog, setOpenFeedDialog] = useState(false);
  const [feedForm, setFeedForm] = useState({
    feed_type_id: '',
    feed_brand_id: '',
    daily_consumption_kg: '',
    source: 'auto',
    feed_procurement_id: '',
    feed_production_id: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  // Decommission dialog
  const [openDecommissionDialog, setOpenDecommissionDialog] = useState(false);
  const [decommissionForm, setDecommissionForm] = useState({
    quantity: '',
    reason: '',
    decommission_date: new Date().toISOString().split('T')[0],
    sale_price_per_bird: '',
    notes: '',
  });

  const handleOpenFeedDialog = (bird?: Bird) => {
    if (!bird) return;
    setSelectedBird(bird);
    setFeedForm({
      feed_type_id: (bird as any).feed_type_id ? String((bird as any).feed_type_id) : '',
      feed_brand_id: (bird as any).feed_brand_id ? String((bird as any).feed_brand_id) : '',
      daily_consumption_kg: '',
      source: 'auto',
      feed_procurement_id: '',
      feed_production_id: '',
    });
    setOpenFeedDialog(true);
  };

  const handleCloseFeedDialog = () => {
    setOpenFeedDialog(false);
    setSelectedBird(null);
    setFeedForm({
      feed_type_id: '',
      feed_brand_id: '',
      daily_consumption_kg: '',
      source: 'auto',
      feed_procurement_id: '',
      feed_production_id: '',
    });
  };

  const handleFeedInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeedForm({ ...feedForm, [e.target.name]: e.target.value });
  };

  const handleAssignFeed = async () => {
    if (!selectedBird) return;

    if (!feedForm.daily_consumption_kg) {
      setSnackbar({
        open: true,
        message: 'Daily consumption is required',
        severity: 'warning',
      });
      return;
    }

    try {
      const payload: any = {
        birds_id: selectedBird.id,
        feed_type_id: parseInt(feedForm.feed_type_id),
        feed_brand_id: parseInt(feedForm.feed_brand_id),
        daily_consumption_kg: parseFloat(feedForm.daily_consumption_kg),
      };

      if (feedForm.source === 'procurement' && feedForm.feed_procurement_id) {
        payload.feed_procurement_id = parseInt(feedForm.feed_procurement_id);
      }

      if (feedForm.source === 'production' && feedForm.feed_production_id) {
        payload.feed_production_id = parseInt(feedForm.feed_production_id);
      }
      await updateFeedInfoMutation.mutateAsync({
        id: selectedBird.id,
        data: {
          feed_type_id: parseInt(feedForm.feed_type_id),
          feed_brand_id: parseInt(feedForm.feed_brand_id),
        },
      });
      setSnackbar({
        open: true,
        message: 'Feed assigned successfully',
        severity: 'success',
      });
      handleCloseFeedDialog();
      refetch();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to assign feed. Please check stock levels and try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      console.error('Failed to assign feed', error);
    }
  };

  // Decommission handlers
  const handleOpenDecommissionDialog = (bird?: Bird) => {
    if (!bird) return;
    setSelectedBird(bird);
    setDecommissionForm({
      quantity: '',
      reason: '',
      decommission_date: new Date().toISOString().split('T')[0],
      sale_price_per_bird: '',
      notes: '',
    });
    setOpenDecommissionDialog(true);
  };

  const handleCloseDecommissionDialog = () => {
    setOpenDecommissionDialog(false);
    setSelectedBird(null);
    setDecommissionForm({
      quantity: '',
      reason: '',
      decommission_date: new Date().toISOString().split('T')[0],
      sale_price_per_bird: '',
      notes: '',
    });
  };

  const handleDecommissionInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDecommissionForm({ ...decommissionForm, [e.target.name]: e.target.value });
  };

  const handleDecommission = async () => {
    if (!selectedBird) return;

    if (!decommissionForm.quantity || !decommissionForm.reason) {
      setSnackbar({
        open: true,
        message: 'Quantity and reason are required',
        severity: 'warning',
      });
      return;
    }

    const quantity = parseInt(decommissionForm.quantity);
    if (quantity > selectedBird.current_quantity) {
      setSnackbar({
        open: true,
        message: `Cannot decommission ${quantity} birds. Only ${selectedBird.current_quantity} birds available.`,
        severity: 'error',
      });
      return;
    }

    try {
      const payload: any = {
        birds_id: selectedBird.id,
        quantity: quantity,
        reason: decommissionForm.reason,
        decommission_date: decommissionForm.decommission_date,
        notes: decommissionForm.notes || undefined,
      };

      if (decommissionForm.sale_price_per_bird) {
        payload.sale_price_per_bird = parseFloat(decommissionForm.sale_price_per_bird);
      }

      await createDecommissionMutation.mutateAsync(payload);
      handleCloseDecommissionDialog();
      refetch();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to decommission birds';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      console.error('Failed to decommission birds', error);
    }
  };

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Failed to Load Birds Data
            </Typography>
            <Typography variant="body2" paragraph>
              {error instanceof Error
                ? error.message
                : 'An error occurred while fetching birds data. Please try again.'}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => refetch()} sx={{ color: '#fff' }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(birdsFetching || statsFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}
      {/* Page header */}
      <Box
        sx={{
          mb: 3.5,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #161616 0%, #1f1f1f 100%)',
          border: '1px solid rgba(255,152,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ff9800', width: 52, height: 52 }}>
          <PetsIcon fontSize="medium" />
        </Avatar>
        <Box flex={1}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: '#ffffff', letterSpacing: '-0.01em' }}
          >
            Bird Batches
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.25 }}>
            Manage flocks, assign feed and track batch performance.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            color: '#000',
            fontWeight: 700,
            borderRadius: '10px',
            '&:hover': { background: 'linear-gradient(135deg, #ffb300 0%, #ff9800 100%)' },
          }}
        >
          + Add Batch
        </Button>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Box display="flex" gap={2.5} mb={4} flexWrap="wrap">
          <Paper
            sx={{
              p: 2.5,
              minWidth: 160,
              flex: 1,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(255,152,0,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(255,152,0,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,152,0,0.12)',
                  color: '#ff9800',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <PetsIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(255,152,0,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  TOTAL BIRDS
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#ff9800', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.total_birds.toLocaleString()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  all batches
                </Typography>
              </Box>
            </Box>
          </Paper>
          <Paper
            sx={{
              p: 2.5,
              minWidth: 160,
              flex: 1,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(76,175,80,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(76,175,80,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(76,175,80,0.12)',
                  color: '#4caf50',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <AliveIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(76,175,80,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  ALIVE BIRDS
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#4caf50', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.current_alive_birds.toLocaleString()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  currently alive
                </Typography>
              </Box>
            </Box>
          </Paper>
          <Paper
            sx={{
              p: 2.5,
              minWidth: 160,
              flex: 1,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(33,150,243,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(33,150,243,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(33,150,243,0.12)',
                  color: '#2196f3',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <EggIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(33,150,243,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  LAYING BIRDS
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#2196f3', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.laying_birds.toLocaleString()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  active layers
                </Typography>
              </Box>
            </Box>
          </Paper>
          <Paper
            sx={{
              p: 2.5,
              minWidth: 160,
              flex: 1,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(156,39,176,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(156,39,176,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(156,39,176,0.12)',
                  color: '#9c27b0',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <TrendingUpIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(156,39,176,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  LAYING RATE
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#9c27b0', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.laying_percentage.toFixed(1)}%
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  of alive birds
                </Typography>
              </Box>
            </Box>
          </Paper>
          <Paper
            sx={{
              p: 2.5,
              minWidth: 160,
              flex: 1,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(102,187,106,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(102,187,106,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(102,187,106,0.12)',
                  color: '#66bb6a',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <TrendingUpIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(102,187,106,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  SURVIVAL RATE
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#66bb6a', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.survival_rate.toFixed(2)}%
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  since procurement
                </Typography>
              </Box>
            </Box>
          </Paper>
          <Paper
            sx={{
              p: 2.5,
              minWidth: 160,
              flex: 1,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(244,67,54,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(244,67,54,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(244,67,54,0.12)',
                  color: '#f44336',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <TrendingDownIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(244,67,54,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  MORTALITY RATE
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#f44336', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.mortality_rate.toFixed(2)}%
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  total loss
                </Typography>
              </Box>
            </Box>
          </Paper>
          <Paper
            sx={{
              p: 2.5,
              minWidth: 160,
              flex: 1,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(255,152,0,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(255,152,0,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,152,0,0.12)',
                  color: '#ff9800',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <GroupsIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(255,152,0,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  TOTAL GROUPS
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#ffffff', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.total_groups}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  active batches
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Table */}
      <Paper
        sx={{
          width: '100%',
          border: '1px solid rgba(255, 152, 0, 0.3)',
          overflow: 'hidden',
        }}
      >
        <DataGrid
          loading={isLoading}
          rows={birdsData?.data || []}
          columns={[
            {
              field: 'id',
              headerName: 'ID',
              width: 70,
              minWidth: 70,
              renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" fontWeight={600} color="primary">
                  {params.value}
                </Typography>
              ),
            },
            {
              field: 'name',
              headerName: 'Batch Name',
              flex: 1.2,
              minWidth: 180,
              renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" fontWeight={600}>
                  {params.value}
                </Typography>
              ),
            },
            {
              field: 'birds_type',
              headerName: 'Type',
              flex: 1,
              minWidth: 120,
              renderCell: (params: GridRenderCellParams) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Chip
                    label={params.value}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(131, 65, 5, 0.1)',
                      color: '#834105',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                </div>
              ),
            },
            {
              field: 'color',
              headerName: 'Color',
              flex: 0.8,
              minWidth: 100,
              renderCell: (params: GridRenderCellParams) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Chip
                    label={params.value}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 152, 0, 0.1)',
                      color: '#ff9800',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                </div>
              ),
            },
            {
              field: 'current_quantity',
              headerName: 'Current Quantity',
              flex: 1,
              minWidth: 130,
              type: 'number',
              renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {params.value.toLocaleString()} birds
                </Typography>
              ),
            },
            {
              field: 'age',
              headerName: 'Age',
              flex: 0.8,
              minWidth: 100,
              type: 'number',
              renderCell: (params: GridRenderCellParams) => (
                <>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    {params.value}
                  </Typography>
                </>
              ),
            },
            {
              field: 'average_weight',
              headerName: 'Avg. Weight (kg)',
              flex: 1,
              minWidth: 140,
              type: 'number',
              renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {params.value?.toLocaleString() ?? 0} kg
                </Typography>
              ),
            },
            {
              field: 'is_laying',
              headerName: 'Status',
              flex: 0.8,
              minWidth: 110,
              renderCell: (params: GridRenderCellParams) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Chip
                    label={params.value ? 'Laying' : 'Not Laying'}
                    color={params.value ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 600, minWidth: 90 }}
                  />
                </div>
              ),
            },
            {
              field: 'feed_type_name',
              headerName: 'Feed Type',
              flex: 1,
              minWidth: 130,
              renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {params.value || 'N/A'}
                </Typography>
              ),
            },
            {
              field: 'feed_estimate',
              headerName: 'Feed Estimate (kg/day)',
              flex: 1,
              minWidth: 160,
              type: 'number',
              renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {params.value !== null ? `${params.value.toLocaleString()} kg` : 'N/A'}
                </Typography>
              ),
            },
            {
              field: 'actions',
              headerName: 'Actions',
              width: 200,
              minWidth: 200,
              sortable: false,
              renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/birds/${params.row.id}`)}
                    sx={{
                      bgcolor: 'rgba(131, 65, 5, 0.1)',
                      '&:hover': { bgcolor: 'rgba(131, 65, 5, 0.2)' },
                    }}
                  >
                    <ViewIcon fontSize="small" sx={{ color: '#834105' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(params.row)}
                    sx={{
                      bgcolor: 'rgba(25, 118, 210, 0.1)',
                      '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.2)' },
                    }}
                  >
                    <EditIcon fontSize="small" sx={{ color: '#1976d2' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenFeedDialog(params.row)}
                    sx={{
                      bgcolor: 'rgba(255, 152, 0, 0.08)',
                      '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.12)' },
                    }}
                  >
                    <FeedIcon fontSize="small" sx={{ color: '#ff9800' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDecommissionDialog(params.row)}
                    sx={{
                      bgcolor: 'rgba(156, 39, 176, 0.1)',
                      '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.2)' },
                    }}
                    title="Decommission birds"
                  >
                    <DecommissionIcon fontSize="small" sx={{ color: '#9c27b0' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(params.row.id)}
                    disabled={deleteMutation.isPending}
                    sx={{
                      bgcolor: 'rgba(211, 47, 47, 0.1)',
                      '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.2)' },
                      '&.Mui-disabled': { bgcolor: 'rgba(0, 0, 0, 0.05)' },
                    }}
                  >
                    <DeleteIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                  </IconButton>
                </Box>
              ),
            },
          ]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 50, page: 0 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
              py: 1.5,
            },
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'rgba(255, 152, 0, 0.15)',
              borderBottom: '2px solid rgba(255, 152, 0, 0.5)',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                fontSize: '0.875rem',
                color: '#ffffff',
              },
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                bgcolor: 'rgba(131, 65, 5, 0.04)',
              },
              '&.Mui-selected': {
                bgcolor: 'rgba(131, 65, 5, 0.08)',
                '&:hover': {
                  bgcolor: 'rgba(131, 65, 5, 0.12)',
                },
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '2px solid rgba(224, 224, 224, 0.4)',
              bgcolor: 'rgba(0, 0, 0, 0.02)',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
          }}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedBird ? 'Edit Bird Batch' : 'Add New Bird Batch'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Procurement ID"
              name="procurement_id"
              type="number"
              value={formData.procurement_id}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Batch Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Bird Type"
              name="type"
              select
              value={formData.type}
              onChange={handleInputChange}
              fullWidth
              required
            >
              <MenuItem value="broiler">Broiler</MenuItem>
              <MenuItem value="layer">Layer</MenuItem>
              <MenuItem value="breeder">Breeder</MenuItem>
            </TextField>
            <TextField
              label="Color"
              name="color"
              select
              value={formData.color}
              onChange={handleInputChange}
              fullWidth
              required
            >
              <MenuItem value="white">White</MenuItem>
              <MenuItem value="brown">Brown</MenuItem>
              <MenuItem value="black">Black</MenuItem>
              <MenuItem value="mixed">Mixed</MenuItem>
            </TextField>
            <TextField
              label="Quantity"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={createMutation.isPending}>
            {createMutation.isPending ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feed Assignment Dialog */}
      <Dialog open={openFeedDialog} onClose={handleCloseFeedDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Feed to Batch</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Feed Type"
              name="feed_type_id"
              select
              value={feedForm.feed_type_id}
              onChange={handleFeedInputChange}
              fullWidth
              required
            >
              {(feedTypes || []).map((t: any) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Feed Brand"
              name="feed_brand_id"
              select
              value={feedForm.feed_brand_id}
              onChange={handleFeedInputChange}
              fullWidth
              required
            >
              {(feedBrands || []).map((b: any) => (
                <MenuItem key={b.id} value={String(b.id)}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Source"
              name="source"
              select
              value={feedForm.source}
              onChange={handleFeedInputChange}
              fullWidth
            >
              <MenuItem value="auto">Auto (aggregate stock)</MenuItem>
              <MenuItem value="procurement">Procured (select lot)</MenuItem>
              <MenuItem value="production">Produced (in-house)</MenuItem>
            </TextField>

            {feedForm.source === 'procurement' && (
              <TextField
                label="Procurement"
                name="feed_procurement_id"
                select
                value={feedForm.feed_procurement_id}
                onChange={handleFeedInputChange}
                fullWidth
              >
                {(feedProcurements?.data || [])
                  .filter((p: any) => p.status === 'delivered')
                  .map((p: any) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.procurement_number} — {p.procurement_date}
                    </MenuItem>
                  ))}
              </TextField>
            )}

            {feedForm.source === 'production' && (
              <TextField
                label="Production"
                name="feed_production_id"
                select
                value={feedForm.feed_production_id}
                onChange={handleFeedInputChange}
                fullWidth
              >
                {(feedProductions || []).map((pr: any) => (
                  <MenuItem key={pr.id} value={String(pr.id)}>
                    {pr.production_number} — {pr.production_date}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeedDialog}>Cancel</Button>
          <Button
            onClick={handleAssignFeed}
            variant="contained"
            disabled={updateFeedInfoMutation.isPending}
          >
            {updateFeedInfoMutation.isPending ? <CircularProgress size={24} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decommission Dialog */}
      <Dialog
        open={openDecommissionDialog}
        onClose={handleCloseDecommissionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Decommission Birds</DialogTitle>
        <DialogContent>
          {selectedBird && (
            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              <Alert severity="info">
                Decommissioning <strong>{selectedBird.name}</strong>. Current quantity:{' '}
                <strong>{selectedBird.current_quantity}</strong> birds
              </Alert>
              <TextField
                label="Quantity to Decommission"
                name="quantity"
                type="number"
                value={decommissionForm.quantity}
                onChange={handleDecommissionInputChange}
                fullWidth
                required
                slotProps={{ htmlInput: { min: 1, max: selectedBird.current_quantity } }}
                helperText={`Maximum: ${selectedBird.current_quantity} birds`}
              />
              <TextField
                label="Reason"
                name="reason"
                select
                value={decommissionForm.reason}
                onChange={handleDecommissionInputChange}
                fullWidth
                required
              >
                <MenuItem value="spent">Spent (End of laying cycle)</MenuItem>
                <MenuItem value="sold">Sold for meat</MenuItem>
                <MenuItem value="low_productivity">Low productivity</MenuItem>
                <MenuItem value="health_issues">Health issues</MenuItem>
                <MenuItem value="culling">Culling</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
              <TextField
                label="Decommission Date"
                name="decommission_date"
                type="date"
                value={decommissionForm.decommission_date}
                onChange={handleDecommissionInputChange}
                fullWidth
                required
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label="Sale Price Per Bird (Optional)"
                name="sale_price_per_bird"
                type="number"
                value={decommissionForm.sale_price_per_bird}
                onChange={handleDecommissionInputChange}
                fullWidth
                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                helperText="If birds were sold, enter the price per bird"
              />
              <TextField
                label="Notes (Optional)"
                name="notes"
                value={decommissionForm.notes}
                onChange={handleDecommissionInputChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Additional notes about this decommission..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDecommissionDialog}>Cancel</Button>
          <Button
            onClick={handleDecommission}
            variant="contained"
            color="secondary"
            disabled={createDecommissionMutation.isPending}
          >
            {createDecommissionMutation.isPending ? 'Decommissioning...' : 'Decommission Birds'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BirdsPage;
